import React, { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import axiosInstance from "../services/baseurl";
import { DeleteUserWithOTP, updateUserEndPoint } from "../services/endpoints";
import { EditIcon, DeleteIcon } from "../assets/icon";
import styles from "../css/listDesign.module.css";

export default function UserActions({ user, onUpdated, onDeleted }) {
  const [mode, setMode] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestPending = useRef(false);
  const openEdit = () => {
    setDraft({ userId: user.userId, name: user.name ?? "", phone: user.phone ?? "", address: user.address ?? "", status: user.status ?? "", role: user.role ?? "" });
    setError(""); setMode("edit");
  };
  const close = () => { if (!requestPending.current) { setMode(null); setError(""); } };
  const submit = async event => {
    event.preventDefault();
    if (requestPending.current) return;
    const phone = String(mode === "edit" ? draft.phone : user.phone).trim();
    if (!/^\d{10}$/.test(phone)) { setError("Enter a valid 10-digit mobile number."); return; }
    if (mode === "edit" && !String(draft.name).trim()) { setError("Enter the user's name."); return; }
    requestPending.current = true; setBusy(true); setError("");
    try {
      const payload = mode === "edit" ? { ...draft, name: draft.name.trim(), phone } : { phone };
      const response = mode === "edit"
        ? await axiosInstance.put(updateUserEndPoint, payload)
        : await axiosInstance.post(DeleteUserWithOTP, payload);
      if (response.data.status !== "Success") throw new Error(response.data.message || "The action could not be completed.");
      if (mode === "edit") onUpdated({ ...user, ...payload });
      else onDeleted(user.userId);
      setMode(null);
    } catch (err) { setError(err.response?.data?.message || err.message || "Please try again."); }
    finally { requestPending.current = false; setBusy(false); }
  };
  const change = key => event => setDraft(previous => ({ ...previous, [key]: event.target.value }));
  return <>
    <div className={styles.userActions}>
      <button type="button" onClick={openEdit} aria-label={`Edit ${user.name || `user ${user.userId}`}`} disabled={busy}><EditIcon /></button>
      <button type="button" onClick={() => { setMode("delete"); setError(""); }} aria-label={`Delete ${user.name || `user ${user.userId}`}`} disabled={busy || !user.phone} title={!user.phone ? "A mobile number is required to delete this user" : "Delete user"}><DeleteIcon /></button>
    </div>
    <Modal show={Boolean(mode)} onHide={close} centered backdrop={busy ? "static" : true} keyboard={!busy} aria-labelledby={`user-action-title-${user.userId}`} className={styles.userModal}>
      <form onSubmit={submit}>
        <Modal.Header closeButton={!busy}><Modal.Title id={`user-action-title-${user.userId}`}>{mode === "edit" ? "Edit user" : "Delete user?"}</Modal.Title></Modal.Header>
        <Modal.Body>
          {mode === "edit" ? <>
            <p className={styles.modalIntro}>Update details for user #{user.userId}.</p>
            <div className={styles.editFields}>
              <label>Name<input value={draft.name || ""} onChange={change("name")} autoComplete="name" required disabled={busy} /></label>
              <label>Mobile number<input value={draft.phone || ""} onChange={change("phone")} inputMode="tel" autoComplete="tel" maxLength={10} pattern="[0-9]{10}" required disabled={busy} /></label>
              <label className={styles.fullField}>Address<textarea value={draft.address || ""} onChange={change("address")} rows={3} autoComplete="street-address" disabled={busy} /></label>
              <label>Status<input aria-label="Status" aria-describedby={`user-status-help-${user.userId}`} value={draft.status || ""} onChange={change("status")} disabled={busy} /><small id={`user-status-help-${user.userId}`}>Keep the current status code unless changing it.</small></label>
              <label>Role<input aria-label="Role" aria-describedby={`user-role-help-${user.userId}`} value={draft.role || ""} onChange={change("role")} disabled={busy} /><small id={`user-role-help-${user.userId}`}>Keep the current role unless changing it.</small></label>
            </div>
          </> : <div className={styles.deleteMessage}><strong>{user.name || `User #${user.userId}`}</strong><span>{user.phone}</span><p>This will permanently delete this user's account. Are you sure you want to continue?</p></div>}
          {error && <p role="alert" className={styles.modalError}>{error}</p>}
        </Modal.Body>
        <Modal.Footer><Button type="button" variant="secondary" onClick={close} disabled={busy}>Cancel</Button><Button type="submit" variant="danger" disabled={busy}>{busy ? mode === "edit" ? "Saving..." : "Deleting..." : mode === "edit" ? "Save changes" : "Yes, delete user"}</Button></Modal.Footer>
      </form>
    </Modal>
  </>;
}
